import React from "react";
import { PageHero, SectionHead, Marquee } from "../editorial/Layout";

const AboutStory = () => (
  <section className="ed-section" style={{ paddingTop: 72 }}>
    <style>{`
      .story-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
      .story-text { font-family: 'Fraunces', serif; font-weight: 300; font-size: 36px; line-height: 1.25; letter-spacing: -0.015em; color: var(--ink); }
      .story-text em { font-style: italic; color: var(--clay); }
      .story-text + .story-text { margin-top: 40px; }
      .story-r { padding-top: 8px; }
      .story-r .lbl { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.18em; text-transform: uppercase; margin-bottom: 18px; }
      .story-r p { font-size: 15px; line-height: 1.6; color: var(--ink-2); margin: 0 0 20px; }
      .story-quote { margin-top: 40px; padding: 28px 30px; background: var(--paper); border-radius: 6px; font-family: 'Fraunces', serif; font-style: italic; font-size: 22px; line-height: 1.35; }
      .story-quote .a { display: block; margin-top: 14px; font-family: 'JetBrains Mono', monospace; font-style: normal; font-size: 11px; color: var(--mute); letter-spacing: 0.12em; }
      @media (max-width: 960px) { .story-grid { grid-template-columns: 1fr; gap: 36px; } .story-text { font-size: 26px; } }
    `}</style>
    <div className="story-grid">
      <div>
        <p className="story-text">
          Founded in 2020, CiTi Batiment is a Cannes interior renovation studio whose <em>only</em> objective is customer satisfaction.
        </p>
        <p className="story-text">
          Each project has a distinct character — a careful structuring of <em>simple things</em> that change the way a home feels to live in.
        </p>
      </div>
      <div className="story-r">
        <div className="lbl">— The studio in two paragraphs</div>
        <p>
          Every house and apartment deserves to be transformed into a beautiful, comfortable, functional living space. That belief is the brief we set ourselves, every time.
        </p>
        <p>
          Our mission is to develop society through our craft — creating unique, personalised spaces that meet both the aesthetic and the functional. Our team is passionate, dedicated, and small enough that you know everyone by name. We work closely with each client to understand what they actually need, not just what they say they want, then build it to a standard we'd photograph.
        </p>
        <div className="story-quote">
          "We don't sell renovations. We sell the months that come after — the cooking, the bathing, the having-people-over."
          <span className="a">— Tudor Cheibas, founder &amp; project lead</span>
        </div>
      </div>
    </div>
  </section>
);

const AboutImages = () => (
  <section style={{ padding: "64px 96px", borderTop: "1px solid var(--line)" }}>
    <style>{`
      .ai-grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr; grid-template-rows: 280px 280px; gap: 20px; }
      .ai-grid > div { border-radius: 6px; background-size: cover; background-position: center; position: relative; overflow: hidden; }
      .ai-grid .a1 { grid-row: 1 / 3; }
      .ai-grid .cap { position: absolute; left: 16px; bottom: 14px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--paper); letter-spacing: 0.16em; background: rgba(0,0,0,0.5); padding: 6px 10px; backdrop-filter: blur(6px); border-radius: 2px; text-transform: uppercase; }
      @media (max-width: 960px) { .ai-grid { grid-template-columns: 1fr; grid-template-rows: auto; } }
    `}</style>
    <div className="ai-grid">
      <div className="a1" style={{ backgroundImage: "url(/assets/firstAbout.jpg)" }}><div className="cap">Studio · Cannes</div></div>
      <div style={{ backgroundImage: "url(/assets/marmura.jpg)" }}><div className="cap">Marble · Mougins</div></div>
      <div style={{ backgroundImage: "url(/assets/parchet.jpg)" }}><div className="cap">Parquet · Antibes</div></div>
      <div style={{ backgroundImage: "url(/assets/plumber.jpg)" }}><div className="cap">Plumbing trade</div></div>
      <div style={{ backgroundImage: "url(/assets/vopsit.jpg)" }}><div className="cap">Paint &amp; finish</div></div>
    </div>
  </section>
);

const AboutStats = () => (
  <section className="ed-section" style={{ background: "var(--ink)", color: "var(--paper)" }}>
    <style>{`
      .as-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; }
      .as-cell { padding: 40px 32px; border-right: 1px solid rgba(251,248,242,0.15); position: relative; }
      .as-cell:last-child { border-right: 0; }
      .as-cell .n { font-family: 'Fraunces', serif; font-weight: 300; font-size: 96px; letter-spacing: -0.035em; line-height: 0.95; color: var(--paper); }
      .as-cell .n em { font-style: italic; font-size: 36px; color: var(--sand); margin-left: 6px; }
      .as-cell .lbl { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: rgba(251,248,242,0.6); letter-spacing: 0.16em; text-transform: uppercase; margin-top: 18px; line-height: 1.5; }
      .as-head { padding: 0 32px 32px; display: flex; justify-content: space-between; align-items: end; border-bottom: 1px solid rgba(251,248,242,0.15); margin: 0 -32px 0; }
      .as-head .h { font-family: 'Fraunces', serif; font-weight: 300; font-size: 52px; letter-spacing: -0.02em; line-height: 1; }
      .as-head .h em { font-style: italic; color: var(--sand); }
      .as-head .r { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: rgba(251,248,242,0.6); letter-spacing: 0.16em; text-transform: uppercase; }
      @media (max-width: 960px) { .as-grid { grid-template-columns: 1fr 1fr; } .as-cell .n { font-size: 56px; } .as-head .h { font-size: 32px; } }
    `}</style>
    <div className="as-head">
      <h2 className="h">Six years, <em>by the numbers</em>.</h2>
      <span className="r">Updated · May 2026</span>
    </div>
    <div className="as-grid" style={{ marginTop: 48 }}>
      <div className="as-cell"><div className="n">150<em>+</em></div><div className="lbl">Homes delivered<br />since 2020</div></div>
      <div className="as-cell"><div className="n">4.9<em>/5</em></div><div className="lbl">Average client<br />review (Google · Houzz)</div></div>
      <div className="as-cell"><div className="n">12<em> wk</em></div><div className="lbl">Average kitchen<br />build time</div></div>
      <div className="as-cell"><div className="n">0<em></em></div><div className="lbl">Hidden contractor<br />mark-ups, ever</div></div>
    </div>
  </section>
);

const AboutTimeline = () => {
  const items = [
    { y: "2020", t: "Studio founded", d: "Tudor opens a one-room office on rue d'Antibes. Three contractors on the books." },
    { y: "2021", t: "25th project delivered", d: "Croisette penthouse — our first cover feature in Maison & Décoration." },
    { y: "2022", t: "XTone & Fitwall partnerships", d: "Certified installers for Porcelanosa's large-format porcelain and Fitwall panels." },
    { y: "2023", t: "Studio of 8", d: "Site managers, interior design lead, marble specialist. Same single-point-of-contact model." },
    { y: "2024", t: "100th home", d: "Marble suite in Mougins. Average rating still 4.9/5 across 70+ reviews." },
    { y: "2026", t: "Booking Q3", d: "Currently taking briefs for Q3 starts. Capacity capped at 9 active builds at once." },
  ];
  return (
    <section className="ed-section">
      <style>{`
        .tl-grid { display: grid; grid-template-columns: 1fr; gap: 0; }
        .tl-row { display: grid; grid-template-columns: 140px 200px 1fr 1fr; gap: 40px; padding: 28px 0; border-top: 1px solid var(--line); align-items: baseline; }
        .tl-row:last-child { border-bottom: 1px solid var(--line); }
        .tl-row .y { font-family: 'Fraunces', serif; font-weight: 400; font-size: 42px; letter-spacing: -0.02em; line-height: 1; }
        .tl-row .n { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--mute); letter-spacing: 0.16em; text-transform: uppercase; }
        .tl-row h3 { font-family: 'Fraunces', serif; font-weight: 400; font-size: 22px; letter-spacing: -0.01em; margin: 0; }
        .tl-row p { font-size: 14.5px; line-height: 1.55; color: var(--ink-2); margin: 0; }
        @media (max-width: 960px) { .tl-row { grid-template-columns: 1fr; gap: 6px; } }
      `}</style>
      <SectionHead
        num="03"
        eyebrow="A short history"
        title='From one office<br/>to <em>a hundred and fifty homes</em>.'
        right=""
      />
      <div className="tl-grid">
        {items.map((it, i) => (
          <div key={it.y} className="tl-row">
            <span className="y">{it.y}</span>
            <span className="n">Nº 0{i + 1}</span>
            <h3>{it.t}</h3>
            <p>{it.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

function AboutDetails() {
  return (
    <main className="page-enter">
      <PageHero
        eyebrow="The studio · 02 of 06"
        title={<>The art of <em>quiet</em> renovation<span style={{ color: "var(--clay)" }}>.</span></>}
        lede="A Cannes interior renovation studio. Eight people, one project lead per home, a hundred and fifty finished projects, and an obsession with the months that come after the keys."
        meta={[
          <><strong>Est.</strong> 2020 · Cannes, FR</>,
          <><strong>Studio</strong> 12 rue d'Antibes</>,
          <><strong>Currently</strong> booking Q3 2026</>,
        ]}
      />
      <AboutStory />
      <AboutImages />
      <AboutStats />
      <AboutTimeline />
      <Marquee items={["Cannes", "Mougins", "Antibes", "Cap d'Antibes", "Villefranche", "Nice", "Saint-Laurent-du-Var", "Mouans-Sartoux"]} />
    </main>
  );
}

export default AboutDetails;
